import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  IDispatchChartListRequest,
  IDispatchChartListResponse,
  IDispatchListRequest,
  IDispatchListResponse,
  IOutgoingCategoriesResponse,
  IPendingReceptionRequest,
  IPendingReceptionResponse,
  IPrepareDispatchChartRequest,
  IPrepareDispatchChartResponse,
  IProcessDispatchChartRequest,
  IReceptionConfirmationRequest,
  IRemoveFromChartRequest,
  IDocumentParcels,
  IParcelPreview,
  IResendParcelRequest,
  IResendParcelResponse,
} from "dto/Mailroom/IDispatchChart";

// Biuro podawcze — przesyłki wychodzące: kategorie/zakładki, karty nadawcze i zwrotki ZPO.
// Dedykowane endpointy (poza DocumentsView) — patrz OutgoingParcelsRepository.DispatchChart.

const dispatchChartQueryKeys = {
  categories: ["outgoing.categories"],
  dispatchList: (category: number, postGroup: number | null | undefined) => [
    "outgoing.dispatchList",
    category,
    postGroup ?? null,
  ],
  charts: (category: number | null | undefined, includeDispatched: boolean) => [
    "outgoing.charts",
    category ?? null,
    includeDispatched,
  ],
  pendingReception: (category: number | null | undefined) => [
    "outgoing.pendingReception",
    category ?? null,
  ],
};

const getCategories = (): Promise<{ data: IOutgoingCategoriesResponse }> =>
  axiosInstance.get("outgoingParcel/categories");

const useGetOutgoingCategories = () =>
  useCustomQuery(dispatchChartQueryKeys.categories, getCategories);

const getDispatchList = (
  request: IDispatchListRequest,
): Promise<{ data: IDispatchListResponse }> =>
  axiosInstance.post("outgoingParcel/dispatchList", request);

const useGetDispatchList = (request: IDispatchListRequest, enabled = true) =>
  useCustomQuery(
    dispatchChartQueryKeys.dispatchList(request.Category, request.PostGroup),
    () => getDispatchList(request),
    { enabled },
  );

const getDispatchCharts = (
  request: IDispatchChartListRequest,
): Promise<{ data: IDispatchChartListResponse }> =>
  axiosInstance.post("outgoingParcel/dispatchChart/list", request);

const useGetDispatchCharts = (
  request: IDispatchChartListRequest,
  enabled = true,
) =>
  useCustomQuery(
    dispatchChartQueryKeys.charts(
      request.Category,
      !!request.IncludeDispatched,
    ),
    () => getDispatchCharts(request),
    { enabled },
  );

const getPendingReception = (
  request: IPendingReceptionRequest,
): Promise<{ data: IPendingReceptionResponse }> =>
  axiosInstance.post("outgoingParcel/receptionConfirmation/pending", request);

const useGetPendingReception = (
  request: IPendingReceptionRequest,
  enabled = true,
) =>
  useCustomQuery(
    dispatchChartQueryKeys.pendingReception(request.Category),
    () => getPendingReception(request),
    { enabled },
  );

const prepareDispatchChart = (
  request: IPrepareDispatchChartRequest,
): Promise<{ data: IPrepareDispatchChartResponse }> =>
  axiosInstance.post("outgoingParcel/dispatchChart/prepare", request);

const usePrepareDispatchChart = () => useCustomMutation(prepareDispatchChart);

const processDispatchChart = (
  request: IProcessDispatchChartRequest,
): Promise<{ data: unknown }> =>
  axiosInstance.post("outgoingParcel/dispatchChart/process", request);

const useProcessDispatchChart = () => useCustomMutation(processDispatchChart);

const removeFromChart = (
  request: IRemoveFromChartRequest,
): Promise<{ data: unknown }> =>
  axiosInstance.post("outgoingParcel/dispatchChart/remove", request);

const useRemoveFromChart = () => useCustomMutation(removeFromChart);

const saveReceptionConfirmation = (
  request: IReceptionConfirmationRequest,
): Promise<{ data: unknown }> =>
  axiosInstance.post("outgoingParcel/receptionConfirmation", request);

const useSaveReceptionConfirmation = () =>
  useCustomMutation(saveReceptionConfirmation);

const resendParcel = (
  request: IResendParcelRequest,
): Promise<{ data: IResendParcelResponse }> =>
  axiosInstance.post("outgoingParcel/resend", request);

const useResendParcel = () => useCustomMutation(resendParcel);

const getParcelPreview = (
  parcelId: string,
): Promise<{ data: IParcelPreview }> =>
  axiosInstance.get(`outgoingParcel/preview/${parcelId}`);

const useGetParcelPreview = (parcelId: string) =>
  useCustomQuery(
    ["outgoing.preview", parcelId],
    () => getParcelPreview(parcelId),
    { enabled: !!parcelId },
  );

const getDocumentParcels = (
  documentId: string,
): Promise<{ data: IDocumentParcels }> =>
  axiosInstance.get(`outgoingParcel/byDocument/${documentId}`);

const useGetDocumentParcels = (documentId: string, enabled = true) =>
  useCustomQuery(
    ["outgoing.byDocument", documentId],
    () => getDocumentParcels(documentId),
    { enabled: enabled && !!documentId, staleTime: 30000 },
  );

/** Wydruk PDF kart nadawczych (blob do pobrania) — endpoint dostarcza Faza C. */
const printDispatchCharts = (chartIds: string[]): Promise<{ data: Blob }> =>
  axiosInstance.post(
    "outgoingParcel/dispatchChart/print",
    { ChartIds: chartIds },
    { responseType: "blob" },
  );

export default {
  dispatchChartQueryKeys,
  useGetOutgoingCategories,
  useGetDispatchList,
  useGetDispatchCharts,
  useGetPendingReception,
  usePrepareDispatchChart,
  useProcessDispatchChart,
  useRemoveFromChart,
  useSaveReceptionConfirmation,
  useResendParcel,
  useGetParcelPreview,
  useGetDocumentParcels,
  printDispatchCharts,
};
